import { Row, Col, Table } from "react-bootstrap";
import { useFetch } from "hooks/api";
import Loading from "components/core/blocks/Loading";
import { formatCurrency, formatNumber } from "utils/formatNumber";
import moment from "moment";
import { keepPreviousData } from "@tanstack/react-query";

export const PricingPlanHistoryTab = ({ pricingPlan, marketPlace }) => {
  const filters = {
    pricing_plan_id: pricingPlan?.id,
  };
  const { data: historyData, isLoading: historyLoading } = useFetch(
    ["pricingplan_history", marketPlace.marketPlace, pricingPlan?.id],
    `/pricing/pricingplan/history`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: pricingPlan !== null && pricingPlan !== undefined,
    },
  );
  if (!pricingPlan) {
    return null;
  }

  if (historyLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  const CurrencyFormatter = (value) => {
    return formatCurrency(value, marketPlace.marketPlace);
  };

  const field = {
    plan_name: {
      name: "Plan Name",
      formatter: (value) => value,
    },
    max_price: {
      name: "Max Price",
      formatter: CurrencyFormatter,
    },
    min_price: {
      name: "Min Price",
      formatter: CurrencyFormatter,
    },
    target_bsr: {
      name: "Target BSR",
      formatter: (value) => formatNumber(value, { decimalPlaces: 0 }),
    },
    configured_slider_value: {
      name: "Strategy",
      formatter: (value) => {
        if (value === -2) {
          return "Maximize Volume";
        } else if (value === -1) {
          return "BoostVolume";
        } else if (value === 0) {
          return "Balanced";
        } else if (value === 2) {
          return "Boost Margin";
        } else {
          return "Maximize Margin";
        }
      },
    },
    status: {
      name: "Plan Status",
      formatter: (value) => {
        if (value === "enabled") {
          return "Active";
        } else if (value === "disabled") {
          return "Disabled";
        } else {
          return "Unknown";
        }
      },
    },
    relationship_rules: {
      name: "Relationship rules",
      formatter: (value) => {
        const entries = Object.entries(value);
        if (entries.length === 0) {
          return "None";
        } else {
          return entries.map(([key, rule]) => `${key}: ${rule.action} by ${rule.value} ${rule.unit}`);
        }
      },
    },
  };

  const DiffRow = ({ history, createdOn, user }) => {
    const rows = [];

    const keys = Object.keys(history);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = history[key];

      if (value?.previous === null || value?.new === null) {
        continue;
      }

      rows.push(
        <tr key={i}>
          <td>{field[key].name}</td>
          <td>{moment(createdOn).format("YYYY-MM-DD HH:mm A")}</td>
          <td className="text-end">{field[key].formatter(value.previous)}</td>
          <td className="text-end">{field[key].formatter(value.new)}</td>
          <td className="text-end">{`${user.first_name} ${user.last_name} (${user.email})`}</td>
        </tr>,
      );
    }

    return rows;
  };

  return (
    <Row className="p-3">
      <Col>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th className="text-start">Field</th>
              <th className="text-start">Date</th>
              <th className="text-end">Previous Value</th>
              <th className="text-end">New Value</th>
              <th className="text-end">User</th>
            </tr>
          </thead>

          <tbody>
            {historyData?.results?.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center p-5">
                  The Pricing Plan has not been recently updated.
                </td>
              </tr>
            )}
            {historyData?.results?.map((history, index) => (
              <DiffRow history={history?.diff} key={index} createdOn={history?.created_on} user={history.updated_by} />
            ))}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};
